import React from 'react';
import styled from 'styled-components';
import { projects, techIcons, statusLabels } from '../data/projects';
import { Section, Card } from '../components/common';
import { Project } from '../types';

const ProjectsContainer = styled(Section)`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.background} 0%, ${({ theme }) => theme.colors.surface} 50%, ${({ theme }) => theme.colors.background} 100%);
  position: relative;
`;

const ProjectsContent = styled.div`
  .section-title {
    font-size: ${({ theme }) => theme.fontSizes['4xl']};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.navyBlue}, ${({ theme }) => theme.colors.lightNavy});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-align: center;

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: ${({ theme }) => theme.fontSizes['3xl']};
    }
  }

  .section-subtitle {
    font-size: ${({ theme }) => theme.fontSizes.lg};
    color: ${({ theme }) => theme.colors.textSecondary};
    text-align: center;
    max-width: 600px;
    margin: 0 auto ${({ theme }) => theme.spacing['3xl']};
    line-height: 1.7;
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.xl};
  align-items: stretch;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const ProjectCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }

  .project-header {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
  }

  .project-icon {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
    color: ${({ theme }) => theme.colors.lightNavy};
    flex-shrink: 0;
  }

  .project-title {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    color: ${({ theme }) => theme.colors.textPrimary};
    margin-right: auto;
  }

  .project-tagline {
    font-size: ${({ theme }) => theme.fontSizes.base};
    color: ${({ theme }) => theme.colors.textPrimary};
    line-height: 1.6;
  }

  .project-problem {
    font-size: ${({ theme }) => theme.fontSizes.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: 1.6;
    border-left: 2px solid ${({ theme }) => theme.colors.navyBlue};
    padding-left: ${({ theme }) => theme.spacing.md};
  }

  .project-features {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.xs};

    li {
      font-size: ${({ theme }) => theme.fontSizes.sm};
      color: ${({ theme }) => theme.colors.textSecondary};
      line-height: 1.5;
      padding-left: ${({ theme }) => theme.spacing.lg};
      position: relative;

      &::before {
        content: '▹';
        position: absolute;
        left: 0;
        color: ${({ theme }) => theme.colors.lightNavy};
      }
    }
  }

  .project-tech {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing.xs};
    margin-top: auto;
    padding-top: ${({ theme }) => theme.spacing.sm};
  }
`;

const StatusBadge = styled.span<{ status: Project['status'] }>`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid ${({ status, theme }) =>
    status === 'completed' ? theme.colors.lightNavy : theme.colors.border};
  color: ${({ status, theme }) =>
    status === 'completed' ? theme.colors.lightNavy : theme.colors.textSecondary};
`;

const TechChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};

  svg {
    color: ${({ theme }) => theme.colors.lightNavy};
  }
`;

export const ProjectsSection: React.FC = () => {
  return (
    <ProjectsContainer id="projects">
        <ProjectsContent>
          <h2 className="section-title">Projects</h2>
          <p className="section-subtitle">
            Things I've built end-to-end — from problem to shipped product.
          </p>

          <ProjectsGrid>
            {projects.map((project) => (
              <ProjectCard key={project.id} hover>
                <div className="project-header">
                  {React.createElement(project.icon as any, { className: 'project-icon' })}
                  <h3 className="project-title">{project.title}</h3>
                  <StatusBadge status={project.status}>
                    {statusLabels[project.status]}
                  </StatusBadge>
                </div>

                <p className="project-tagline">{project.description}</p>

                {project.problem && (
                  <p className="project-problem">{project.problem}</p>
                )}

                <ul className="project-features">
                  {project.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>

                <div className="project-tech">
                  {project.technologies.map((tech) => {
                    const Icon = techIcons[tech];
                    return (
                      <TechChip key={tech}>
                        {Icon && React.createElement(Icon as any, { size: 12 })}
                        {tech}
                      </TechChip>
                    );
                  })}
                </div>
              </ProjectCard>
            ))}
          </ProjectsGrid>
        </ProjectsContent>
    </ProjectsContainer>
  );
};
